import * as React from "react";
import queryString from "query-string";

import { useDispatch, useSelector } from "react-redux";
import { appActions, userInfoActions } from "../actions";
import { classNames, retrieveLangPack, checkStep } from "../utils";
import api, { IApplicationResponse } from "../utils/api";
import { IUserInfo } from "../types/appDataTypes";
import { TSteps } from "../reducers/appReducers";
import * as AppTypes from "AppTypes";
import { retrieveDocuments } from "../utils/retrieveDocuments";
import { TLanguage } from "../reducers/configReducers";
import { updateLanguageAATagging, updateAATagging } from "../utils/aatagging";
import { detectBrowser } from "../utils/detection";
import { DesktopDateOfBirthSelect } from "../components/DateOfBirthFieldDesktop/DesktopDateOfBirthSelect";
import { toString } from "lodash";

const parsedSearch = queryString.parse(window.location.search) as {
  country: IUserInfo["activeCountry"];
  verify: string;
  captchaToken: string;
  jumper: TSteps;
};

let timeoutId = 0;

const Verify: React.FC = () => {
  const dispatch = useDispatch();

  const [isWarning, setIsWarning] = React.useState(false);
  const [appId, setAppId] = React.useState(null);
  const [errorCount, setErrorCount] = React.useState(0);
  const [countDown, setCountDown] = React.useState(0);

  const { langPack } = useSelector((store: AppTypes.ReducerState) => ({
    langPack: store.config.langPack,
  }));

  const handleBirthDayInput = async (values: {
    date?: number;
    year?: number;
    month?: number;
  }) => {
    const date = toString(values.date).padStart(2, "0");
    const year = toString(values.year);
    const month = toString(values.month + 1).padStart(2, "0");

    const dob = `${year}-${month}-${date}`;

    const response = await api.checkBirthday({ dob, appId });
    if (response && response.status === 200) {
      const userInfoResponse = await api.getApplication(appId);
      if (userInfoResponse && userInfoResponse.status === 200) {
        const { data } = userInfoResponse;
        data.appId = appId;
        retrieveDocuments(data);

        dispatch(userInfoActions.updateUserInfo({ ...data, appId }));

        // retrieve language setting
        if (data.lang) {
          retrieveLangPack(
            data.lang.toUpperCase() as TLanguage,
            data.activeCountry.toUpperCase() as IUserInfo["activeCountry"]
          );
        }
        const nextStep = checkStep(data);
        dispatch(appActions.gotoStep(nextStep));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname +
            `?country=${data.activeCountry.toUpperCase()}`
        );

        // AA Tracking
        const AALang = updateLanguageAATagging(langPack["lang"]);
        updateAATagging(
          {
            event_name: "generic_form_complete_step1",
            form_name: "UOB PoY",
            language: AALang,
            country: parsedSearch.country.toLowerCase(),
            step_name: "continuation_verification_page",
          },
          "other_form_step"
        );
      }
    } else {
      setIsWarning(true);
      setCountDown(countDown + 15 * errorCount);
      setErrorCount(errorCount + 1);
    }
  };

  React.useEffect(() => {
    window.clearTimeout(timeoutId);
    if (countDown > 0) {
      timeoutId = window.setTimeout(() => setCountDown(countDown - 1), 1000);
    }
  }, [countDown]);

  const checkTokenValidity = async () => {
    const result = await api.checkValidity(
      parsedSearch.verify,
      parsedSearch.captchaToken
    );

    if (result && result.status === 200) {
      const data = result.data as IApplicationResponse;
      const { appId } = data;
      setAppId(appId);
    }
  };

  React.useEffect(() => {
    if (!detectBrowser()) {
      dispatch(
        appActions.gotoStep("UnsupportedBrowser", {
          title: langPack["errorDetectBrowserTitle"],
          content: langPack["errorDetectBrowserContent"],
        })
      );
    } else {
      checkTokenValidity();
    }
  }, []);

  return (
    <div className="content content--center" style={{ alignItems: "center" }}>
      <h2 className="app-title">{langPack["labelVerification"]}</h2>
      <p className="app-sub-title">{langPack["enterBirthDate"]}</p>
      <p
        className="verify-count-down"
        style={{
          // position: "relative",
          bottom: "-2.5em",
          visibility: !!countDown ? "visible" : "hidden",
          marginTop: "30px",
        }}
      >
        {"Try again in {number} seconds.".replace(
          "{number}",
          countDown.toString()
        )}
      </p>
      <DesktopDateOfBirthSelect
        disabled={!!countDown}
        handleSubmit={handleBirthDayInput}
      />
      <p
        className={classNames({
          "app-description": true,
          "app-warning": isWarning,
        })}
        style={{ maxWidth: 300, marginTop: "1.5rem" }}
      >
        {isWarning ? langPack["INVALID_DOB"] : ""}
      </p>
      <div className="action">
        {langPack["competitionDisclaimer"] && (
          <>
            <p className="app-disclaimer">
              {langPack["competitionDisclaimerText"]}
            </p>
            <br />
          </>
        )}
      </div>
    </div>
  );
};

export default Verify;
